<template>
  <section id="ROICalc">
    <div class="container">
      <h2 class="bigTitle">
        Calculate ROI
      </h2>
      <div id="simpleROI" class="has-text-centered">
        <pac-columns>
          <div class="column is-two-thirds sliderCol">
            <client-only>
              <div :style="{ 'backgroundPosition': fancyBG}" class="order-vol-slider">
                <label for="orderVol">How many orders do you fulfill per month, via parcel?</label>
                <vue-slider
                  v-model="orderVol"
                  :min="100"
                  :max="100000"
                  :interval="100"
                  :marks="volMarks"
                  :tooltip-formatter="tooltipFormatter"
                />
              </div>
            </client-only>
          </div>
          <div class="column is-one-sixth roiEstimate">
            <div class="estimateBlock">
              <div class="has-text-centered">Est. Paccurate Cost / Month</div>
              <div class="simpleCostEstimate">{{ estPrice | moneyMaker }}</div>
            </div>
            <div class="estimateBlock">
              <div class="has-text-centered priceHeader">Est. Net Parcel Savings / Month</div>
              <div class="simpleEstSavings">{{ estSavings | moneyMaker }}+</div>
            </div>
          </div>
        </pac-columns>
      </div>
      <div id="advancedROI">
        <pac-columns>
          <div class="column">
            <h2 style="margin-bottom: 1em;">Advanced Controls</h2>
            <label for="multiVol">Monthly multi-item <span data-tooltip="What percentage of your outgoing orders have more than 1 item?"><font-awesome-icon icon="question-circle" /></span></label>
            <vue-slider v-model="multiVol" :marks="multiPerc" :tooltip-formatter="tooltipFormatter2" />
            <br><br>
            <label for="multiVol">Avg Per-Shipment Spend <span data-tooltip="How much does shipping cost for each order, on average?"><font-awesome-icon icon="question-circle" /></span></label>
            <vue-slider
              v-model="perShipmentCost"
              :min="5"
              :max="100"
              :interval="5"
              :marks="multiMoney"
              :tooltip-formatter="tooltipFormatter1"
            />
            <br><br>
            <h2>Prove It</h2>
            <pac-columns class="subcol">
              <div class="column"><p>
                This estimator is based on historical savings in successful
                analyses and implementations. Your milage may vary. <a @click="seen = !seen">See Savings Breakdown</a>.
              </p></div>
              <div class="column"><p>
                For a more accurate ROI, grab an API key and run analysis on your historical shipping data. Let us know if we can help!
              </p></div>
            </pac-columns>

            <!-- <h3>Savings Assumptions</h3>
            <label for="errorSavings">Savings from general error reduction</label>
            <vue-slider v-model="errorSavings" :marks="multiPerc" :max="50" :tooltip-formatter="tooltipFormatter2" />
            <br> -->
            <!-- <label for="costAwareSavings">Savings from "costs" awareness</label>
            <vue-slider v-model="costAwareSavings" :marks="multiPerc" :max="50" :tooltip-formatter="tooltipFormatter2" /> -->
          </div>
          <div class="column is-four-sixths">
            <div id="envelopeMath">
              <h3 style="margin-top: 0px;">Back of the envelope math</h3>
              <table>
                <tbody>
                  <tr>
                    <td>Avg Shipping Spend / Mo. <br> <span>(Order Vol X Avg Shipment $)</span></td>
                    <td class="costFactor">{{ monthlyShipSpend | moneyMaker }}</td>
                  </tr>
                  <tr>
                    <td>Paccurate Cost / Mo. <br><span>(Unique Requests X Volume Tier Rate)</span></td>
                    <td class="costFactor">+ {{ estPrice | moneyMaker }}</td>
                  </tr>
                  <tr>
                    <td>Savings from error reduction <br> <span>(ErrorSavings% X Order Vol)</span> </td>
                    <td class="saveFactor">- {{ errorTotal | moneyMaker }}</td>
                  </tr>
                  <tr>
                    <td>Savings from rate awareness <br> <span>(RateSavings% X Multi-Line Order Vol)</span> </td>
                    <td class="saveFactor">- {{ costAwareTotal | moneyMaker }}</td>
                  </tr>
                  <tr id="netSavings">
                    <td>Net Monthly Savings</td>
                    <td class="saveFactor totalSave">{{ estSavings | moneyMaker }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

        </pac-columns>
        <div v-if="seen" id="breakItDown">
          <h2 style="margin-left: 1em;">Break It Down</h2>
          <pac-columns>
            <div class="column"><strong>Error Reduction:</strong> (9% - 25%) Computers are better at packing, and don't make mistakes like even very experienced humans do. Taking the guesswork away can supercharge even seasonal packers.</div>
            <div class="column"><strong>Cost Awareness:</strong> (Conservatively 6%) One of the unique core features of Paccurate is its ability to consider costs when making a packing decision. This might be box cost, labor cost, or even the complex incentive patterns in your negotiated rate tables.</div>
            <div class="column"><strong>Time/Labor Savings:</strong> Faster packing means more throughput. Time saved is hard to generalize and is outside the scope of this calculator.</div>
          </pac-columns>
        </div>
      </div>
    </div>
  </section>
</template>

<script>

import 'vue-slider-component/theme/antd.css'
export default {
  filters: {
    moneyMaker (val) {
      return '$' + val.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')
    }
  },
  data () {
    return {
      seen: false,
      orderVol: 100,
      multiVol: 25,
      perShipmentCost: 10,
      monthlyShipSpend: 0,
      estSavings: 0,
      estPrice: 0,
      perTcost: 0.5,
      costAwareSavings: 6,
      costAwareTotal: 0,
      errorSavings: 10,
      errorTotal: 0,
      fancyBG: '0% 79%',
      floorPrice: 25,
      volMarks: {
        100: '100',
        10000: '10k',
        25000: '25k',
        50000: '50k',
        75000: '75k',
        100000: '100k'
      },
      tooltipFormatter: v => `${('' + v).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`,
      tooltipFormatter1: v => `$${('' + v).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`,
      tooltipFormatter2: v => `${('' + v)}%`,
      multiPerc: val => val % 10 === 0 ? ({
        label: `${val}%`,
        labelStyle: {
          opacity: val * 0.01 * 0.7 + 0.3
        },
        labelActiveStyle: {
          color: '#3498db'
        }
      }) : false,
      multiMoney: val => val % 10 === 0 ? ({
        label: `$${val}`
      }) : false
    }
  },
  computed: {
    changeData () {
      const { orderVol, multiVol, perShipmentCost, errorSavings, costAwareSavings } = this
      return {
        orderVol,
        multiVol,
        perShipmentCost,
        errorSavings,
        costAwareSavings
      }
    },
    changeBG () {
      const { orderVol } = this
      return {
        orderVol
      }
    }
  },
  watch: {
    changeData: {
      handler (val) {
        this.calcSavings()
      },
      deep: true
    },
    changeBG: {
      handler (val) {
        const newColor = ((val.orderVol / 100000) * 100).toFixed().toString()
        this.fancyBG = newColor + '% 50%'
      }
    }
  },
  mounted () {
    this.calcSavings()
  },
  methods: {
    calcSavings () {
      this.monthlyShipSpend = this.orderVol * this.perShipmentCost
      this.pricePerRequest(this.orderVol)
      this.errorTotal = (this.errorSavings / 100) * this.monthlyShipSpend
      this.costAwareTotal = (this.costAwareSavings / 100) * (this.monthlyShipSpend * (this.multiVol / 100))
      this.estSavings = (this.errorTotal + this.costAwareTotal) - this.estPrice
    },
    pricePerRequest (val) {
      let onTopofMin = 0
      // console.log(val)

      if (val >= 1000000) {
        this.perTcost = 0.018
        this.floorPrice = 21110
        onTopofMin = val - 1000000
      } else if (val >= 100000 && val <= 999999) {
        this.perTcost = 0.02
        this.floorPrice = 3110
        onTopofMin = val - 100000
      } else if (val >= 10000 && val <= 99999) {
        this.perTcost = 0.03
        this.floorPrice = 410
        onTopofMin = val - 10000
      } else if (val >= 1000 && val <= 9999) {
        this.perTcost = 0.04
        this.floorPrice = 50
        onTopofMin = val - 1000
      } else if (val >= 0 && val <= 999) {
        this.perTcost = 0.05
        this.floorPrice = 25
        onTopofMin = val - 500
      }

      if (onTopofMin < 0) {
        this.estPrice = this.floorPrice
      } else {
        this.estPrice = this.floorPrice + (onTopofMin * this.perTcost)
      }
    }
  }
}
</script>

<style scoped>
#showAdvanced{
  cursor: pointer;
}
.fa-question-circle{
  color: #999;
  font-size: 0.85rem;
  margin-left: 0.4rem;
}

.vue-slider{
  margin-top: 0.8rem;
  margin-bottom: 0.5rem;
}

/* Add this attribute to the element that needs a tooltip */
[data-tooltip] {
  position: relative;
  z-index: 2;
  cursor: pointer;
}

/* Hide the tooltip content by default */
[data-tooltip]:before,
[data-tooltip]:after {
  visibility: hidden;
  -ms-filter: "progid:DXImageTransform.Microsoft.Alpha(Opacity=0)";
  filter: progid: DXImageTransform.Microsoft.Alpha(Opacity=0);
  opacity: 0;
  pointer-events: none;
}

/* Position tooltip above the element */
[data-tooltip]:before {
  position: absolute;
  bottom: 150%;
  left: 50%;
  margin-bottom: 5px;
  margin-left: -80px;
  padding: 7px;
  width: 160px;
  -webkit-border-radius: 3px;
  -moz-border-radius: 3px;
  border-radius: 3px;
  background-color: #000;
  background-color: hsla(0, 0%, 20%, 0.9);
  color: #fff;
  content: attr(data-tooltip);
  text-align: center;
  font-size: 14px;
  line-height: 1.2;
}

/* Triangle hack to make tooltip look like a speech bubble */
[data-tooltip]:after {
  position: absolute;
  bottom: 150%;
  left: 50%;
  margin-left: -5px;
  width: 0;
  border-top: 5px solid #000;
  border-top: 5px solid hsla(0, 0%, 20%, 0.9);
  border-right: 5px solid transparent;
  border-left: 5px solid transparent;
  content: " ";
  font-size: 0;
  line-height: 0;
}

/* Show tooltip content on hover */
[data-tooltip]:hover:before,
[data-tooltip]:hover:after {
  visibility: visible;
  -ms-filter: "progid:DXImageTransform.Microsoft.Alpha(Opacity=100)";
  filter: progid: DXImageTransform.Microsoft.Alpha(Opacity=100);
  opacity: 1;
}
</style>
